import { useCallback, useRef, useState } from 'react';
import {
  Label,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
} from 'recharts';
/** Redux */
import { getTextWidth } from '../utils';
import { formatNumberWithComma } from '../../../utils/formatText';
import { useOthersLine, useSingleDateData } from '../hooks';
import useChartLines from '../hooks/useChartLines';
import { QueryType } from '../../../utils/apollo/graphqlSchema';
import {
  defaultNumberFormatOptions,
  globalFormat,
} from '../../../utils/formatting';
import { assertCondition } from '../../../utils/types';
import styled from 'styled-components';
import ResponsiveChartWrapper from '../ResponsiveChartWrapper';
import { ChartMode } from '../types';
import { ChartNumberType } from '../interfaces';
import useCacheSingleDateLlmRawDataEffect from '../hooks/llmCacheRawData/useCacheSingleDateLlmRawDataEffect';
import { theme } from '../../../styles/theme';
import { useCurrentUrl } from '../../../utils/routing';
import { useChartContext } from '../ChartContext';
interface IProps {
  analysisId: string;
  mode: ChartMode;
}

const StyledLabel = styled(Label)`
  font-size: 12px;
  fill: #fff;
`;

// on rechart 2.15, they added a outline to the pie chart when it is focused
// we need to override it to avoid the outline
const StyledPieChart = styled(PieChart)`
  g:focus,
  path:focus {
    outline: none !important;
  }
`;

/**
 * only display label if the percent is greater than 5%
 * otherwise, it will not have enough space to display the label
 */
const MINIMUM_DISPLAY_PERCENT = 0.05;

export default ({ analysisId, mode }: IProps) => {
  const {
    chartData,
    settings: { chartSettings },
    colorConfig,
    metadata: { isBreakdownOverall, timeUnit },
    displayedSlices,
    displayedMeasures,
  } = useChartContext();
  const { visibleLines } = useChartLines({
    chartSettings,
    chartData,
    isBreakdownOverall,
    colorConfig,
  });
  assertCondition(chartData.queryType === QueryType.Expression);
  const { isDashboardInUrl } = useCurrentUrl();
  const isSmallPieChart = mode === ChartMode.Compact || isDashboardInUrl;
  const [linesWithOthers, linesMetaWithOthers, dataWithOthers] = useOthersLine({
    visibleLines,
    chartData,
    chartSettings,
    displayedSlices,
    chartNumberType: ChartNumberType.Number,
  });
  const { parsedData, largestIndex } = useSingleDateData({
    data: dataWithOthers,
    visibleLines: linesWithOthers,
    singleDayLabel: chartSettings.singleDayLabel,
    linesMeta: linesMetaWithOthers,
    ignoreOverall: true,
    chartType: chartSettings.chartType,
    measureControls: chartData.measureControls,
    displayedMeasures,
    queryType: QueryType.Expression,
    timeUnit,
  });
  const chartContainerRef = useRef<any>(null);
  const [activeIndex, setActiveIndex] = useState(largestIndex);

  const customizeFormatter = (value: any) => {
    const fixedPercent = globalFormat.formatNumber(
      value,
      defaultNumberFormatOptions.percent,
    );
    if (value >= MINIMUM_DISPLAY_PERCENT) {
      return fixedPercent;
    }
    return null;
  };

  const renderActiveShape = (pieEvent: any) => {
    const RADIAN = Math.PI / 180;
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      stroke,
      color,
      payload: activePayload,
    } = pieEvent;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 20) * cos;
    const my = cy + (outerRadius + 20) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';
    const fixedPercent = globalFormat.formatNumber(
      activePayload.percent,
      defaultNumberFormatOptions.percent,
    );

    const { label, value } = activePayload;
    const textXCoordinate = ex + (cos >= 0 ? 1 : -1) * 12;
    const measureName = prepareMeasureName(
      label,
      value,
      textXCoordinate,
      textAnchor,
    );

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          stroke={stroke}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
          stroke={stroke}
        />
        <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={color}
          fill="none"
        />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text
          x={textXCoordinate}
          y={ey}
          textAnchor={textAnchor}
          fill={theme.colors.secondary900}
          fontSize={14}
          fontWeight={500}
        >
          {fixedPercent}
        </text>
        <text
          x={textXCoordinate}
          y={ey}
          dy={12}
          textAnchor={textAnchor}
          fill={color}
          fontSize={12}
          fontWeight={600}
        >
          {measureName}
        </text>
      </g>
    );
  };

  const pieMouseEnter = (_: any, index: any) => {
    setActiveIndex(index);
  };

  const prepareMeasureName = useCallback(
    (name: string, value: string, x: number, textAnchor: string): string => {
      const pieChartNameValueString = `${name} : ${formatNumberWithComma(
        value,
      )}`;

      const textWidth = getTextWidth(pieChartNameValueString, '14pt bold');
      const singleCharWidth = textWidth / pieChartNameValueString.length;
      const wrapperContainer = chartContainerRef?.current;
      const containerWidth = wrapperContainer?.state?.containerWidth;
      let isTextOverflowing = false;
      let overflowWidth = 0;

      switch (textAnchor) {
        case 'start': {
          isTextOverflowing = x + textWidth > containerWidth;
          if (isTextOverflowing) {
            overflowWidth = x + textWidth - containerWidth;
          }
          break;
        }
        case 'end': {
          isTextOverflowing = x - textWidth < 0;
          if (isTextOverflowing) {
            overflowWidth = textWidth - x;
          }
          break;
        }
        default:
          break;
      }
      if (!isTextOverflowing) {
        return pieChartNameValueString;
      }
      const charactersNeededToTrim =
        Math.ceil(overflowWidth / singleCharWidth) + 3; // For the ellipsis ...
      const totalLengthAfterTrim =
        pieChartNameValueString.length - charactersNeededToTrim;

      const firstChunk = pieChartNameValueString.slice(
        0,
        totalLengthAfterTrim / 2,
      );
      const lastChunk = pieChartNameValueString.slice(
        pieChartNameValueString.length - totalLengthAfterTrim / 2,
      );

      return `${firstChunk}...${lastChunk}`;
    },
    [],
  );

  useCacheSingleDateLlmRawDataEffect({
    analysisId,
    visibleLines: linesWithOthers,
    data: parsedData,
    chartTypeName: 'Pie',
    singleDayLabel: chartSettings.singleDayLabel,
  });

  return (
    <ResponsiveChartWrapper analysisId={analysisId} mode={mode}>
      <ResponsiveContainer ref={chartContainerRef}>
        <StyledPieChart
          margin={{
            top: 0,
            right: 5,
            bottom: 0,
            left: 5,
          }}
        >
          <Pie
            data={parsedData}
            dataKey="percent"
            nameKey="name"
            cx={'50%'}
            cy={'50%'}
            outerRadius={isSmallPieChart ? 60 : 120}
            activeIndex={activeIndex}
            activeShape={renderActiveShape}
            onMouseEnter={pieMouseEnter}
            isAnimationActive={false}
          >
            <LabelList
              dataKey="percent"
              formatter={customizeFormatter}
              position="inside"
              content={({ content, stroke, ref, ...labelProps }) => {
                // destructuring and skipping the content property from labelProps because it is this function and
                // Label also accepts content property which leads to endless rendering loop
                // overriding the render to override stroke (label main color)
                return <StyledLabel {...labelProps} />;
              }}
            />
          </Pie>
        </StyledPieChart>
      </ResponsiveContainer>
    </ResponsiveChartWrapper>
  );
};
